import React, { useState } from "react";
import { Currency } from "@/types/currency";
import { formatImageUrl, toTitleCase } from "@/lib/general";
import placeholderImage from "@/assets/imgloading.png";
import { typeToIconClass } from "./question";
import { FormData as ExperienceFormData } from "@/types/custom";

interface PreviewCardProps {
  formData: ExperienceFormData;
  selectedCurrency: Currency;
}

const PreviewCard: React.FC<PreviewCardProps> = ({
  formData,
  selectedCurrency,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(true);

  const previewExperience = {
    id: "preview",
    name: formData.title || "Your Activity Name",
    types: formData.categories,
    images: formData.images.map((img: { url?: string; preview: string }) => ({
      url: img.url || img.preview,
    })),
    waytora_place_id: "preview",
  };

  return (
    <div className="flex gap-4 h-full">
      {/* Left column - ExperienceItem preview */}
      <div className="w-[40%] h-full">
        <div className="flex flex-col h-full">
          {/* Image container - 2/3 height */}
          <div className="relative h-full w-full">
            <div className="w-full h-full rounded-lg overflow-hidden">
              {previewExperience.images[currentImageIndex]?.url ? (
                <div className="relative w-full h-full">
                  <img
                    src={formatImageUrl(
                      previewExperience.images[currentImageIndex]?.url ?? "",
                      "high",
                      false
                    )}
                    alt={previewExperience.name}
                    className={`w-full h-full object-cover transition-opacity duration-300 ${
                      isImageLoading ? "opacity-0" : "opacity-100"
                    }`}
                    onLoad={() => setIsImageLoading(false)}
                  />
                  {isImageLoading && (
                    <div className="absolute inset-0 bg-neutral-100 flex items-center justify-center">
                      <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full h-full bg-neutral-100 flex items-center justify-center">
                  <img
                    src={placeholderImage}
                    alt={previewExperience.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right column - Additional details */}
      <div className="flex-1 min-w-0 flex flex-col min-h-[8rem] w-full">
        <div className="w-full h-full flex flex-col">
          <div className="flex items-center flex-wrap gap-2">
            <h2 className="text-base font-medium text-neutral-800 truncate max-w-[calc(100%-8rem)]">
              {formData.title || (
                <span className="opacity-70">Your Activity Name</span>
              )}
            </h2>
          </div>

          {/* Info boxes */}
          <div className="flex gap-3 mt-1.5 mb-2">
            {/* Cost */}
            <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-md 2xl:px-2 2xl:py-1.5">
              <i className="far fa-money-bill text-neutral-700 text-xs 2xl:text-[13px]" />
              <span className="text-[11px] text-neutral-700 font-light 2xl:text-[13px]">
                {formData.cost === "0" ? (
                  "Free"
                ) : (
                  <>
                    From {selectedCurrency.symbol}
                    {formData.cost || <span className="opacity-70">100</span>}
                  </>
                )}
              </span>
            </div>

            {/* Duration */}
            <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-md 2xl:px-2 2xl:py-1.5">
              <i className="far fa-clock text-neutral-700 text-xs 2xl:text-[13px]" />
              <span className="text-[11px] text-neutral-700 font-light 2xl:text-[13px]">
                {formData.duration || <span className="opacity-70">2</span>}{" "}
                hours
              </span>
            </div>
          </div>

          <p className="text-xs 2xl:text-[14px] font-light text-neutral-700 line-clamp-3 mt-1">
            {formData.description || (
              <span className="opacity-70">
                Enter a description of your activity here. This could include
                details about what visitors can expect, any special
                requirements, or interesting facts about the experience.
              </span>
            )}
          </p>

          {/* Categories - Now at bottom */}
          <div className="flex flex-row gap-2 mt-auto overflow-x-auto no-scrollbar">
            {formData.categories.map((category: string, index: number) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-slate-50 px-2 py-1 rounded-md whitespace-nowrap"
              >
                <i
                  className={`${typeToIconClass[category]} text-xs text-neutral-700`}
                />
                <span className="text-[11px] text-neutral-700 font-light">
                  {toTitleCase(category)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewCard;
